import LoadingSpinner from '@common/src/components/LoadingSpinner';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useAnvaFlowsCompanies } from '@flows/src/api/anva-flows/anva-config/getAnvaFlowsCompanies';
import {
  useAnvaFlowsCoverages,
  flattenCoverageCodes,
} from '@flows/src/api/anva-flows/anva-config/getAnvaFlowsCoverages';
import {
  useAnvaProducts,
  AnvaProduct,
  AnvaProductStatus,
} from '@flows/src/api/anva-flows/getAnvaProducts';
import FlowsConfigureCompanyItem from '@flows/src/components/flows-configure/insurances/FlowsConfigureCompanyItem';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import useAnvaInsuranceName from '@flows/src/hooks/flows-configure/useAnvaInsuranceGroupName';
import { useFlowsConfigureParams } from '@flows/src/hooks/flows-configure/useFlowsConfigureParams';
import useFlowsConfigureSelectAllButtonProps from '@flows/src/hooks/flows-configure/useFlowsConfigureSelectAllButtonProps';
import { useFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/flowsConfigureStore';
import { PartyType } from '@parties/src/types';
import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';

export interface CompanyWithStatus {
  id: string;
  name: string;
  code: string;
  form: string;
  form_description: string;
  status?: AnvaProductStatus;
}

const FlowsConfigureCompanies: FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | undefined>(undefined);
  const hasAutoSelectedRef = useRef<Record<string, boolean>>({});
  const { flowId, insuranceId: insuranceGroupId } = useFlowsConfigureParams();
  const { handleDeselectAll, handleSelectAll } =
    useFlowsConfigureSelectAllButtonProps('companies', insuranceGroupId);
  const queryparams = useQueryParams();
  const partyType = (queryparams.get('type') as PartyType) || 'CUSTOMER';
  const insuranceGroups = useFlowsConfigureStore((state) => state.insurances);
  const insuranceGroup = insuranceGroups.find(
    (group) => group.id === Number(insuranceGroupId),
  );
  const { data: companies, isLoading } = useAnvaFlowsCompanies(
    partyType,
    insuranceGroup ? insuranceGroup.insurance_ids : undefined,
    insuranceGroupId
      ? useFlowsConfigureStore.getState().coverages[insuranceGroupId]
      : undefined,
  );

  // Fetch product data to get status information
  const { data: productsData, isLoading: isLoadingProducts } =
    useAnvaProducts(partyType);

  // Create a mapping of company ID to product status
  const companyStatusMap = useMemo(() => {
    if (!productsData?.items || !insuranceGroup) return {};

    const statusMap: Record<string, AnvaProductStatus> = {};

    productsData.items.forEach((product: AnvaProduct) => {
      // Check if this product belongs to the current insurance group
      const belongsToGroup = insuranceGroup.insurance_ids.includes(
        Number(product.insurance.id),
      );

      if (belongsToGroup) {
        const companyId = product.insurance_company.id;
        // If we already have a status for this company, prioritize ACTIVE over UNDER_CONSTRUCTION over DISABLED
        if (
          !statusMap[companyId] ||
          (statusMap[companyId] === 'DISABLED' &&
            product.status !== 'DISABLED') ||
          (statusMap[companyId] === 'UNDER_CONSTRUCTION' &&
            product.status === 'ACTIVE')
        ) {
          statusMap[companyId] = product.status;
        }
      }
    });

    return statusMap;
  }, [productsData, insuranceGroup]);

  // Enhance companies with status information
  const companiesWithStatus: CompanyWithStatus[] = useMemo(() => {
    if (!companies?.items) return [];

    return companies.items.map((company) => ({
      ...company,
      status: companyStatusMap[company.id],
    }));
  }, [companies, companyStatusMap]);

  // Pre-fetch coverage data for all insurance groups
  const coverageQueries = insuranceGroups.map((group) => {
    return useAnvaFlowsCoverages(partyType, group.insurance_ids);
  });

  // Create a map of insurance ID to coverage count
  const coverageCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    insuranceGroups.forEach((group, index) => {
      const coverageData = coverageQueries[index]?.data;
      counts[group.id] = coverageData?.items?.length || 0;
    });
    return counts;
  }, [insuranceGroups, coverageQueries]);

  // Auto-select all ACTIVE companies by default
  useEffect(() => {
    if (
      !isLoading &&
      !isLoadingProducts &&
      companiesWithStatus.length > 0 &&
      insuranceGroupId &&
      !hasAutoSelectedRef.current[insuranceGroupId]
    ) {
      const existingSelections =
        useFlowsConfigureStore.getState().companies[insuranceGroupId];

      // Only auto-select if no companies are currently selected
      if (!existingSelections || existingSelections.length === 0) {
        const activeCompanies = companiesWithStatus
          .filter((company) => company.status === 'ACTIVE')
          .map((company) => company.id);

        if (activeCompanies.length > 0) {
          handleSelectAll(activeCompanies);
          hasAutoSelectedRef.current[insuranceGroupId] = true;
        }
      } else {
        // Mark as auto-selected if there are already selections
        hasAutoSelectedRef.current[insuranceGroupId] = true;
      }
    }
  }, [
    isLoading,
    isLoadingProducts,
    companiesWithStatus,
    insuranceGroupId,
    handleSelectAll,
  ]);

  const isAllSelected = useFlowsConfigureStore((state) => {
    const selectableCompanies = companiesWithStatus.filter(
      (company) => company.status === 'ACTIVE',
    );
    return (
      state.companies[insuranceGroupId || '']?.length ===
        selectableCompanies.length && selectableCompanies.length > 0
    );
  });
  const { insuranceName } = useAnvaInsuranceName(insuranceGroupId || '');
  const history = useHistory();

  if (
    !insuranceGroupId ||
    !insuranceGroups.map(({ id }) => id).includes(Number(insuranceGroupId))
  ) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const currentIndex = insuranceGroups
    .map(({ id }) => id)
    .indexOf(Number(insuranceGroupId));
  const nextInsurance = insuranceGroups[currentIndex + 1]?.id;

  const handlePrevious = () => {
    const currentIndex = insuranceGroups.findIndex(
      (group) => group.id === Number(insuranceGroupId),
    );

    if (currentIndex === 0) {
      // This is the first insurance's companies page
      // We need to go back to the last insurance that had multiple coverages
      let lastCoverageIndex = insuranceGroups.length - 1;

      while (lastCoverageIndex >= 0) {
        const insurance = insuranceGroups[lastCoverageIndex];
        const coverageCount = coverageCounts[insurance.id];

        if (coverageCount > 1) {
          // Found an insurance with multiple coverages, go there
          history.push({
            pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${insurance.id}/coverages`,
            search: window.location.search,
          });
          return;
        }

        // Auto-select single coverage if not already selected
        if (coverageCount === 1) {
          const existingCoverages =
            useFlowsConfigureStore.getState().coverages[String(insurance.id)];
          if (!existingCoverages || existingCoverages.length === 0) {
            const coverageData = coverageQueries[lastCoverageIndex]?.data;
            if (coverageData?.items?.length === 1) {
              useFlowsConfigureStore
                .getState()
                .setCoverages(
                  String(insurance.id),
                  flattenCoverageCodes(coverageData.items),
                );
            }
          } else {
            // Ensure mandatory coverages are always selected
            const coverageData = coverageQueries[lastCoverageIndex]?.data;
            if (coverageData?.items?.length === 1) {
              const mandatoryCoverageCodes = coverageData.items
                .filter((group) => group.mandatory)
                .reduce(
                  (acc: string[], group) => acc.concat(group.coverage_codes),
                  [],
                );

              if (mandatoryCoverageCodes.length > 0) {
                const updatedSelections = [...existingCoverages];
                let hasNewMandatorySelections = false;

                mandatoryCoverageCodes.forEach((code) => {
                  if (!updatedSelections.includes(code)) {
                    updatedSelections.push(code);
                    hasNewMandatorySelections = true;
                  }
                });

                if (hasNewMandatorySelections) {
                  useFlowsConfigureStore
                    .getState()
                    .setCoverages(String(insurance.id), updatedSelections);
                }
              }
            }
          }
        }

        lastCoverageIndex--;
      }

      // If no insurance had multiple coverages, go to insurance selection
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
        search: window.location.search,
      });
    } else {
      // Go to the previous insurance's companies page
      const prevInsurance = insuranceGroups[currentIndex - 1];
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${prevInsurance.id}/companies`,
        search: window.location.search,
      });
    }
  };

  const handleNext = () => {
    const areCompaniesSelected =
      useFlowsConfigureStore.getState().companies?.[insuranceGroupId]?.length >
      0;
    if (!areCompaniesSelected) {
      setError(t('Select at least one option'));
      return;
    }

    if (nextInsurance) {
      // Go to the next insurance's companies page
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${nextInsurance}/companies`,
        search: window.location.search,
      });
    } else {
      // No more insurances, go to finish
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/finish`,
        search: window.location.search,
      });
    }
  };

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('FLOWS_FLOW-CONFIG_INSURANCE-COMPANIES-FOR') + insuranceName}
      error={error}
    >
      {isLoading || isLoadingProducts ? (
        <LoadingSpinner />
      ) : (
        <ConfigureItemsGrid singleColumn={companiesWithStatus.length === 1}>
          {companiesWithStatus && companiesWithStatus.length > 1 && (
            <SelectAllButton
              isAllSelected={isAllSelected}
              onDeselectAll={handleDeselectAll}
              onSelectAll={() =>
                handleSelectAll(
                  companiesWithStatus
                    .filter((company) => company.status === 'ACTIVE')
                    .map((company) => company.id) || [],
                )
              }
            />
          )}
          {companiesWithStatus?.map((company) => (
            <FlowsConfigureCompanyItem
              key={company.id}
              insuranceId={insuranceGroupId}
              company={company}
              isError={!!error}
              clearError={() => setError(undefined)}
            />
          ))}
        </ConfigureItemsGrid>
      )}
    </ContentWrapper>
  );
};

export default FlowsConfigureCompanies;
